import { conversionAttributionUtmValueSchema } from '@galapagos/shared';
import type { SignupDownloadHandoff } from '../services/signup-api/signup-api';
import type { DownloadCampaign, DownloadPlacement } from './download-links';

/**
 * The legacy signup's direct-download link: the flow root opened with a
 * `download` param, as printed QR codes, emails and SMS still send it. Its
 * value means nothing except the literal `false`, which opts out.
 *
 * `download` is the trimmed value the legacy-params parser keeps, `undefined`
 * when the key is absent.
 */
export function isDirectDownload(download: string | undefined): boolean {
  return download !== undefined && download !== 'false';
}

/** Which store a device is sent to, or `other` for the download page alone. */
export type DirectDownloadPlatform = 'ios' | 'android' | 'other';

/**
 * The legacy signup's device test, kept as it is so the same visitors are
 * sent to a store: Android first, then any user agent naming the iPhone (iPod
 * touch says `iPhone OS`). iPads, iPadOS that reports a Mac, computers and
 * every other phone get the page.
 */
export function directDownloadPlatform(userAgent: string): DirectDownloadPlatform {
  if (/android/i.test(userAgent)) return 'android';
  if (/iPhone/.test(userAgent)) return 'ios';
  return 'other';
}

/** A phone's links name the jump to its store; any other device's name the page. */
export function directDownloadPlacement(platform: DirectDownloadPlatform): DownloadPlacement {
  return platform === 'other' ? 'download-link-page' : 'download-link-redirect';
}

/**
 * The campaign labels the link itself carries, for its store links only: its
 * five UTMs and its own `medium`, each kept only when it fits the shared
 * campaign-label rule. Nothing here is stored or sent as attribution; the
 * root landing capture still owns that. `source` and `cta_source` are
 * analytics only and never reach a store link.
 */
export function directDownloadCampaign(search: string): DownloadCampaign {
  const params = new URLSearchParams(search);
  const label = (key: string): string | null => {
    const parsed = conversionAttributionUtmValueSchema.safeParse(params.get(key));
    return parsed.success ? parsed.data : null;
  };
  return {
    utmSource: label('utm_source'),
    utmMedium: label('utm_medium'),
    utmCampaign: label('utm_campaign'),
    utmContent: label('utm_content'),
    utmTerm: label('utm_term'),
    medium: label('medium'),
  };
}

/** The store a phone is sent to from the handoff it was shown, or none for any other device. */
export function directDownloadStoreUrl(
  platform: DirectDownloadPlatform,
  handoff: SignupDownloadHandoff,
): string | null {
  if (platform === 'ios') return handoff.appStoreUrl;
  if (platform === 'android') return handoff.playStoreUrl;
  return null;
}
